import firstValue from "../../../helpers/first.value";
import Container from "../../../components/element/container";
import { useEffect, useState } from "react";
import { MDBModalBody } from "mdb-react-ui-kit";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/element/icon";
import AccountModalHeader from "../../../components/common/account.modal.header";
import useRequireAuth from "../../../components/hook/use.require.auth";
import FullModal from "../../../components/modal/full.modal";
import { showToast } from "../../../helpers/show.toast";
import { closeReferralModal } from "../../../redux/reducer/modal.reducer";
import { getReferral } from "../../../redux/reducer/profile.reducer";


const money = (value) =>
  `RM ${Number(value || 0).toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function ProfileReferralModal({ onShare }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { requireAuth } = useRequireAuth();
  const [copied, setCopied] = useState("");
  const { isOpenReferralModal } = useSelector((state) => state.modal);
  const { profileInfo, referralInfo } = useSelector((state) => state.profile);

  useEffect(() => {
    if (isOpenReferralModal) dispatch(getReferral());
  }, [dispatch, isOpenReferralModal]);

  useEffect(() => {
    if (referralInfo?.code) {
      localStorage.setItem("own_referral_code", referralInfo.code);
    }
  }, [referralInfo?.code]);

  const summary = referralInfo?.summary || {};
  const code =
    firstValue(
      referralInfo?.code,
      profileInfo?.referral?.code,
      profileInfo?.referral_code,
      localStorage.getItem("own_referral_code"),
    ) || "-";
  const link = `${window.location.origin}/?referral_code=${code}`;
  const stats = [
    {
      icon: "solar:users-group-rounded-linear",
      tone: "people",
      value: Number(summary?.reservations_referred || 0),
      label: "Reservations Referred",
    },
    {
      icon: "solar:gift-linear",
      tone: "gift",
      value: money(summary?.balance),
      label: "Reward Balance (Available)",
    },
    {
      icon: "solar:wallet-linear",
      tone: "wallet",
      value: money(summary?.total_earned),
      label: "Total Earned",
    },
  ];

  const close = () => dispatch(closeReferralModal());

  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied(""), 1800);
    } catch {
      showToast(t("Unable to copy"), t("Please copy it manually."), "error");
    }
  };

  const shareVia = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: "ri:whatsapp-fill",
      onClick: () =>
        window.open(
          `https://wa.me/?text=${encodeURIComponent(
            `Reserve your next car with DCAR using my referral link: ${link}`,
          )}`,
          "_blank",
          "noreferrer",
        ),
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: "ri:facebook-fill",
      onClick: () =>
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
          "_blank",
          "noreferrer",
        ),
    },
    {
      key: "telegram",
      label: "Telegram",
      icon: "ri:telegram-fill",
      onClick: () =>
        window.open(
          `https://t.me/share/url?url=${encodeURIComponent(
            link,
          )}&text=${encodeURIComponent(
            "Reserve your next car with DCAR using my referral link",
          )}`,
          "_blank",
          "noreferrer",
        ),
    },
    {
      key: "more",
      label: "More",
      icon: "material-symbols:share-outline",
      onClick: () => onShare?.(),
    },
  ];

  return (
    <FullModal
      independent
      show={isOpenReferralModal}
      close={close}
      showCloseButton
      centered
      scrollable
      screenSize="lg"
      className="product-action-modal profile-info-modal profile-referral-modal"
      dialogClassName="action-modal-dialog profile-referral-dialog"
      contentClassName="action-modal-content profile-referral-content"
      content={
        <MDBModalBody className="profile-referral-body">
          <AccountModalHeader
            title={t("Referral Program")}
            subtitle={t("Invite friends and earn rewards")}
            icon="akar-icons:people-group"
          />

          <section className="profile-referral-code-card">
            <article>
              <span className="profile-referral-label">
                {t("Your Referral Code")}
              </span>
              <Container tag="div">
                <strong className="profile-referral-code">{code}</strong>
                <button
                  type="button"
                  className="profile-referral-copy"
                  aria-label="Copy referral code"
                  onClick={() =>
                    requireAuth(() => copy(code, "code"), { modal: "referral" })
                  }
                >
                  <Icon
                    icon={
                      copied === "code"
                        ? "solar:check-read-linear"
                        : "solar:copy-linear"
                    }
                  />
                </button>
              </Container>
            </article>
            <span className="profile-referral-card-divider" />
            <article>
              <span className="profile-referral-label">
                {t("Share Your Link")}
              </span>
              <Container tag="div">
                <p className="profile-referral-link">{link}</p>
                <button
                  type="button"
                  className="profile-referral-copy"
                  aria-label="Copy referral link"
                  onClick={() =>
                    requireAuth(() => copy(link, "link"), { modal: "referral" })
                  }
                >
                  <Icon
                    icon={
                      copied === "link"
                        ? "solar:check-read-linear"
                        : "solar:copy-linear"
                    }
                  />
                </button>
              </Container>
            </article>
          </section>

          <Container tag="div" className="profile-referral-divider">
            <span />
            <h3>{t("Share via")}</h3>
            <span />
          </Container>

          <Container tag="div" className="profile-referral-share">
            {shareVia.map((item) => (
              <button
                type="button"
                className={`profile-referral-share-item ${item.key}`}
                key={item.key}
                onClick={() => requireAuth(item.onClick, { modal: "referral" })}
              >
                <span>
                  <Icon icon={item.icon} />
                </span>
                {t(item.label)}
              </button>
            ))}
          </Container>

          <section className="profile-referral-stats">
            {stats.map((stat) => (
              <article key={stat.label} className="profile-referral-stat">
                <Container tag="div" className="flex items-center gap-3">
                  <Icon icon={stat.icon} className={`_svg ${stat.tone}`} />
                  <strong>{stat.value}</strong>
                </Container>
                <span>{t(stat.label)}</span>
              </article>
            ))}
          </section>

          <p className="profile-referral-note">
            <Icon icon="solar:info-circle-linear" />
            {t(
              "Rewards are released after the referred reservation is completed. Contact support to claim or withdraw your balance.",
            )}
          </p>
        </MDBModalBody>
      }
    />
  );
}
